/**
 * NotificationToast Component
 * Displays real-time notifications as toasts
 * Respects user's quiet hours settings
 */

import React, { useEffect, useState } from 'react';
import notificationService from '../utils/notificationService';

const isInQuietHours = (start, end) => {
  if (!start || !end) return false;

  const now = new Date();
  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);

  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;
  const nowTime = now.getHours() * 60 + now.getMinutes();

  if (startTime <= endTime) {
    return nowTime >= startTime && nowTime < endTime;
  } else {
    // Quiet hours span midnight
    return nowTime >= startTime || nowTime < endTime;
  }
};

export const NotificationToastContainer = ({ token }) => {
  const [toasts, setToasts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    // Fetch notification settings
    const fetchSettings = async () => {
      try {
        const userSettings = await notificationService.getSettings(token);
        setSettings(userSettings);
      } catch (error) {
        // Silently fail - not critical
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();

    // Subscribe to notification events
    const unsubscribe = notificationService.on('notification', (notification) => {
      // Check if in app notifications are enabled
      if (!settings?.inAppNotifications && settings !== null) {
        return;
      }

      // Check quiet hours
      if (settings?.quietHoursEnabled &&
          isInQuietHours(settings.quietHoursStart, settings.quietHoursEnd)) {
        return;
      }

      // Add toast
      const toastId = `${notification.id}-${Date.now()}`;
      setToasts(prev => [
        {
          id: toastId,
          notification,
          timestamp: Date.now()
        },
        ...prev.slice(0, 4) // Max 5 toasts
      ]);

      // Auto-remove after 6 seconds
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toastId));
      }, 6000);
    });

    return () => {
      unsubscribe();
    };
  }, [token, settings]);

  return (
    <div className="fixed left-4 right-4 sm:left-auto z-[80] space-y-3 pointer-events-none bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md:bottom-4">
      {toasts.map(toast => (
        <NotificationToastItem
          key={toast.id}
          notification={toast.notification}
          onClose={() =>
            setToasts(prev => prev.filter(t => t.id !== toast.id))
          }
        />
      ))}
    </div>
  );
};

const NotificationToastItem = ({ notification, onClose }) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const priorityColors = {
    high: 'bg-red-500/20 border-red-500/40',
    normal: 'bg-blue-500/20 border-blue-500/40',
    low: 'bg-gray-500/20 border-gray-500/40'
  };

  return (
    <div
      className={`
        pointer-events-auto w-full sm:w-80 sm:max-w-sm ml-auto p-4 rounded-lg border
        ${priorityColors[notification.priority] || priorityColors.normal}
        backdrop-blur-xl shadow-lg
        animate-slideInRight
      `}
    >
      <div className="flex gap-3">
        {/* Icon */}
        {notification.icon && (
          <div className="text-xl flex-shrink-0">{notification.icon}</div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-white truncate">
            {notification.title}
          </h4>
          <p className="text-xs text-gray-300 line-clamp-2 mt-1">
            {notification.message}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={() => {
            setDismissed(true);
            onClose();
          }}
          className="flex-shrink-0 text-gray-400 hover:text-white transition-colors ml-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* CTA Button */}
      {notification.ctaButton && notification.actionUrl && (
        <button
          onClick={() => {
            notificationService.trackClick(
              localStorage.getItem('accessToken'),
              notification.id,
              notification.actionUrl
            );
            setDismissed(true);
            onClose();
            window.location.href = notification.actionUrl;
          }}
          className="mt-3 w-full text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded transition"
        >
          {notification.actionText || 'View'}
        </button>
      )}
    </div>
  );
};

// CSS for animation
const toastStyles = `
  @keyframes slideInRight {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .animate-slideInRight {
    animation: slideInRight 0.3s ease-out;
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = toastStyles;
  document.head.appendChild(style);
}

export default NotificationToastContainer;
